import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';
import { ServerTokenService } from '../services/server-token.service';

/**
 * `reasonId` is the reason document's Mongo `_id` in hex form — an opaque string,
 * never a number. It is the same identity the referral site rows carry in
 * `internalDeclineReasonId`, which is what lets a label be resolved off this list.
 *
 * The catalog includes retired reasons (`isActive: false`) because referrals filed
 * under one keep the assignment, and this list is the only place their name
 * exists. Filter on `isActive` to build a picker; resolve labels off the whole list.
 */
export interface InternalDeclineReasonOption {
  reasonId: string;
  name: string;
  isActive: boolean;
  /** The category's reasonId, or null for a category's own row. */
  parentId: string | null;
}

/**
 * A facility row on a referral is addressed by `listSiteId` — the region comes off the
 * caller's token, and (listSiteId, regionId) is the pair that identifies one facility.
 * Not by name: `listSiteName` is a display value that is null until the site is assigned
 * to a facility, and several EHR-side site names can resolve onto the same facility.
 */
export interface AssignInternalDeclineReasonPayload {
  referralId: string;
  listSiteId: number;
  internalDeclineReasonId: string | null;
}

@Injectable({ providedIn: 'root' })
export class InternalDeclineReasonsReferralService {
  private readonly basePath = 'snfs/internal-reasons';

  private sharedReasons$: Observable<InternalDeclineReasonOption[]> | null = null;

  constructor(private server: ServerTokenService) {}

  /** Every reason in the region, retired ones included. See the DTO note above. */
  getReasons(): Observable<InternalDeclineReasonOption[]> {
    if (!this.sharedReasons$) {
      const request$ = this.server.getWithToken<InternalDeclineReasonOption[]>(this.basePath).pipe(
        // Drop the cache on failure, otherwise shareReplay latches the error forever.
        catchError((error) => {
          if (this.sharedReasons$ === request$) {
            this.sharedReasons$ = null;
          }
          return throwError(error);
        }),
        shareReplay(1),
      );
      this.sharedReasons$ = request$;
    }
    return this.sharedReasons$;
  }

  resetCache(): void {
    this.sharedReasons$ = null;
  }

  assign(payload: AssignInternalDeclineReasonPayload): Observable<void> {
    return this.server.putWithToken<void>(`${this.basePath}/assign`, payload);
  }
}
